import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  FetchAuthSessionOptions,
  fetchUserAttributes,
  getCurrentUser,
  signIn,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolClientId: process.env.REACT_APP_COGNITO_USER_CLIENT_ID || '',
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
    },
  },
});

export type UserSession = {
  username: string;
  tokens: {
    IdToken: string;
    AccessToken: string;
    RefreshToken: string;
  };
};

export type AuthenticateProps = {
  username?: string;
  password?: string;
  remember?: boolean;
  oldPassword?: string;
};

export type UserAttributes = {
  [key: string]: string;
};

// Amplify resolves with empty tokens instead of throwing when there is no
// session or the refresh token has been revoked.
const getSession = async (
  options?: FetchAuthSessionOptions
): Promise<UserSession> => {
  const { tokens } = await fetchAuthSession(options);
  if (!tokens?.idToken || !tokens.accessToken) {
    throw new Error('no session');
  }
  const { username } = await getCurrentUser();

  return {
    tokens: {
      AccessToken: tokens.accessToken.toString(),
      IdToken: tokens.idToken.toString(),
      RefreshToken: '',
    },
    username,
  };
};

export const login = async (props: AuthenticateProps) => {
  const username = (props.username || '').toLowerCase();

  // signIn throws UserAlreadyAuthenticatedException if tokens are still stored.
  try {
    await signOut();
  } catch {
    // ignore
  }

  const { isSignedIn, nextStep } = await signIn({
    password: props.password,
    username,
  });
  if (!isSignedIn) {
    throw new Error(`Unsupported sign-in step: ${nextStep.signInStep}`);
  }

  return getSession();
};

export const logout = () => {
  signOut().catch(() => undefined);
};

export const getUserSessionAttributes = async (): Promise<
  [UserSession, UserAttributes]
> => {
  const session = await getSession();
  const result = await fetchUserAttributes();

  const attributes: UserAttributes = {};
  for (const [key, value] of Object.entries(result)) {
    if (value !== undefined) {
      attributes[key] = value;
    }
  }
  return [session, attributes];
};

export const refreshSession = (): Promise<UserSession> => {
  return getSession({ forceRefresh: true });
};

export const changePassword = async (props: AuthenticateProps) => {
  await getSession();
  await updatePassword({
    newPassword: props.password || '',
    oldPassword: props.oldPassword || '',
  });
  return 'SUCCESS' as const;
};
